/**
 * Computes the total height of every element currently acting as a
 * top-anchored sticky/fixed bar (navbar + top ad banner + sticky tabs
 * if any). Anchor scrolling helpers use it so the target section never
 * lands hidden behind the navigation chrome.
 *
 * Opt-in via `data-sticky-offset="true"` on the element you want measured.
 * The header is auto-detected (`header[data-sticky="navbar"]` or the
 * default `<header>` that has `position: sticky` and `top: 0`).
 *
 * Add an extra breathing-room pixel pad via the second arg (default 8).
 */
export function getStickyOffset(extraPad = 8): number {
  if (typeof window === "undefined" || typeof document === "undefined") return 0;
  let offset = 0;

  // Auto-detect the main header.
  const header = document.querySelector<HTMLElement>("header");
  if (header) {
    const cs = window.getComputedStyle(header);
    if (
      (cs.position === "sticky" || cs.position === "fixed") &&
      parseFloat(cs.top || "0") <= 1
    ) {
      offset += header.offsetHeight;
    }
  }

  // Opt-in stack (top ads, sticky tab bars, etc.) — measured in DOM order.
  const stack = document.querySelectorAll<HTMLElement>(
    '[data-sticky-offset="true"]'
  );
  stack.forEach((el) => {
    if (el === header) return;
    const cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return;
    offset += el.offsetHeight;
  });

  return offset + extraPad;
}

/**
 * Scroll an element into view honouring the sticky offset above. Falls
 * back to native `scrollIntoView` if the element is detached.
 */
export function scrollToElementWithOffset(el: HTMLElement | null, extraPad = 8) {
  if (!el || typeof window === "undefined") return;
  const offset = getStickyOffset(extraPad);
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
}
